// Inventory — the laptop's half of the count (Fernando, 2026-10-08: "we have the snapshot locally so we
// should calculate it locally and then sync the findings back to the server").
//
//   CATALOGUE  the hall map, loaded once when the tool is opened: every barcoded item of the counted shops
//              with its label, size, home section code, restock codes, synced stock and NOS flag, plus the
//              sections' display names (Ä folded onto Z 29). Page memory only — re-Load any time.
//   DECIDE     every scan is decided here against the Start snapshot + the catalogue, the same rules the
//              server applies (backend/wgr_inventory.py _resolve_barcode + the role rule), so the popup is
//              there when the scanner beeps: home / restock shelf / wrong place (with the home shelf) /
//              unknown / ambiguous.
//   QUEUE      scans go to the server in batches in the background with the laptop's clock and their own ids
//              (a resent batch is stored once — the pp/track.ts pattern). The server stays the record and
//              answers with the lines it holds; where it decided differently the screen corrects itself. A
//              410 (session saved / closed by a manager / round closed) stops the count loudly.
import { authFetch, API } from './api';

const INV = `${API}/wgr-inventory`;

// ── the catalogue ─────────────────────────────────────────────────────────────
export interface CatItem {
  wms_id: number; ws: string; platform: 'wgr' | 'shopify'; barcode: string; label: string; size: string;
  home: string; restock: string[]; stock_qty: number; nos: boolean;
  /** the product the size belongs to (master_sku) — the screen groups sizes by it */
  product?: string;
}
export interface Catalogue {
  byBarcode: Map<string, CatItem[]>;
  byId: Map<number, CatItem>;
  sections: Record<string, { display: string; codes: string[] }>;
  etag: string; loadedAt: number; count: number;
}

let current: Catalogue | null = null;
let loading: Promise<Catalogue> | null = null;
const listeners = new Set<() => void>();
function notify() { listeners.forEach(l => { try { l(); } catch { /* a listener must not break the others */ } }); }

export function getCatalogue(): Catalogue | null { return current; }

export async function loadCatalogue(force = false): Promise<Catalogue> {
  if (current && !force) return current;
  if (loading) return loading;
  loading = (async () => {
    const r = await authFetch(`${INV}/catalogue`, { headers: current ? { 'If-None-Match': current.etag } : {} });
    if (r.status === 304 && current) { current = { ...current, loadedAt: Date.now() }; notify(); return current; }
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`);
    }
    const d = await r.json();
    const cols: string[] = d.cols;
    const ix = (name: string) => cols.indexOf(name);
    const iId = ix('wms_id'), iWs = ix('ws'), iPf = ix('platform'), iBc = ix('barcode'), iLb = ix('label'), iSz = ix('size'),
          iHome = ix('home'), iRs = ix('restock'), iSq = ix('stock_qty'), iNos = ix('nos'), iPr = ix('product');
    const byBarcode = new Map<string, CatItem[]>();
    const byId = new Map<number, CatItem>();
    for (const row of d.items as any[][]) {
      const it: CatItem = {
        wms_id: row[iId], ws: d.webshops[row[iWs]], platform: row[iPf] === 's' ? 'shopify' : 'wgr', barcode: row[iBc],
        label: row[iLb], size: row[iSz] || '', home: row[iHome] || '', restock: row[iRs] ? String(row[iRs]).split(',').filter(Boolean) : [],
        stock_qty: row[iSq] || 0, nos: !!row[iNos], product: iPr >= 0 ? (row[iPr] || '') : '',
      };
      byId.set(it.wms_id, it);
      const list = byBarcode.get(it.barcode);
      if (list) list.push(it); else byBarcode.set(it.barcode, [it]);
    }
    current = { byBarcode, byId, sections: d.sections || {}, etag: d.etag, loadedAt: Date.now(), count: byId.size };
    notify();
    return current;
  })();
  try { return await loading; } finally { loading = null; }
}

/** The server resolved a barcode the laptop did not know (a WGR lookup) or knows better: remember it for the next scan. */
export function patchCatalogue(it: CatItem) {
  if (!current) return;
  const list = (current.byBarcode.get(it.barcode) || []).filter(x => x.wms_id !== it.wms_id);
  list.push(it);
  current.byBarcode.set(it.barcode, list);
  current.byId.set(it.wms_id, it);
}

export function subscribeCatalogue(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The display name of a section key ('Z29' → 'Z 29'; a code the plan lacks → 'R 70'). */
export function sectionDisplay(cat: Catalogue | null, code: string): string {
  if (!code) return '';
  const s = cat?.sections[code];
  if (s) return s.display;
  const m = /^([A-ZÅÄÖ]{1,2})(\d{1,4})$/.exec(code);
  return m ? `${m[1]} ${parseInt(m[2], 10)}` : code;
}

/** The section KEY a shelf code belongs to ('Ä' → 'Z29'). */
export function sectionKey(cat: Catalogue | null, code: string): string {
  if (!code || !cat) return code;
  if (cat.sections[code]) return code;
  for (const [key, s] of Object.entries(cat.sections)) if (s.codes.includes(code)) return key;
  return code;
}

// ── the decision: the same rules as the server ────────────────────────────────
export type LocalStatus = 'home' | 'overflow' | 'misplaced' | 'ambiguous' | 'unknown_ean' | 'pending';
export interface LocalDecision { status: LocalStatus; item: CatItem | null }

/** backend _resolve_barcode, line for line: one match → it; several → the one homed here (→ the stocked one
 *  across shops / the single garment of one shop) → the one overflowing here → the stocked one → ambiguous. */
export function decideScan(ean: string, codes: string[], cat: Catalogue | null): LocalDecision {
  if (!cat) return { status: 'pending', item: null };
  const cands = cat.byBarcode.get(ean) || [];
  if (cands.length === 0) return { status: 'unknown_ean', item: null };
  const roleOf = (it: CatItem): LocalStatus =>
    codes.includes(it.home) ? 'home' : it.restock.some(x => codes.includes(x)) ? 'overflow' : 'misplaced';
  if (cands.length === 1) return { status: roleOf(cands[0]), item: cands[0] };
  const stockedOne = (rows: CatItem[]) => {
    if (new Set(rows.map(c => c.ws)).size > 1) {
      const st = rows.filter(c => c.stock_qty > 0);
      if (st.length === 1) return st[0];
    }
    return null;
  };
  const here = cands.filter(c => codes.includes(c.home));
  if (here.length === 1) return { status: 'home', item: here[0] };
  if (here.length > 1) {
    const s = stockedOne(here);
    if (s) return { status: 'home', item: s };
    if (new Set(here.map(c => c.ws)).size === 1 && new Set(here.map(c => `${c.label}|${c.size}`)).size === 1) return { status: 'home', item: here[0] };
    return { status: 'ambiguous', item: null };
  }
  const over = cands.filter(c => c.restock.some(x => codes.includes(x)));
  if (over.length === 1) return { status: 'overflow', item: over[0] };
  const s = stockedOne(cands);
  if (s) return { status: roleOf(s), item: s };
  return { status: 'ambiguous', item: null };
}

/** backend _color, line for line. */
export function colorOf(expected: number, scanned: number, belongsHere: boolean, finalized: boolean, infinite = false, skipped = false): string {
  if (!belongsHere) return 'yellow';
  if (skipped) return 'skipped';
  if (infinite) return scanned > 0 ? 'gray' : 'white';
  if (scanned > expected) return 'blue';
  if (expected > 0 && scanned === expected) return 'green';
  if (scanned === 0) return finalized && expected > 0 ? 'purple' : 'white';
  return finalized ? 'red' : 'orange';
}

// ── the queue ────────────────────────────────────────────────────────────────
export interface QueuedScan { cid: string; ean: string; at: string; local: { status: LocalStatus; wms_id: number | null } }
export interface ScanResult {
  cid: string; status: string; wms_id?: number; platform?: string; label?: string; size?: string | null; home?: string | null;
  home_code?: string | null; restock?: string; duplicate: boolean; agree: boolean; unskipped?: boolean; infinite?: boolean; ean?: string; message?: string;
}
export interface LineState {
  wms_id: number; role: string; scanned_saved_qty: number; ongoing_qty: number; overflow_qty: number; counted_total: number;
  expected_qty: number; color: string; infinite: boolean; skipped: boolean; moved_out_qty: number; moved_in_qty: number;
}
export interface QueueHandlers {
  onResults: (results: ScanResult[], lines: LineState[], sent: QueuedScan[]) => void;
  onSessionGone: (reason: string, unsent: QueuedScan[]) => void;
  onPending: (n: number, offline: boolean) => void;
  /** a batch the server refused for good (4xx): its scans are NOT recorded — the screen must say so and re-read */
  onError: (message: string, dropped: QueuedScan[]) => void;
}

const FLUSH_MS = 600;
const MAX_BATCH = 40;
const RETRY_MS = [1000, 2000, 5000, 10000, 30000];

export function newCid(): string {
  try { if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID(); } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export class ScanQueue {
  private pending: QueuedScan[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryStep = 0;
  private fails = 0;
  private sending = false;
  private stopped = false;
  private key: string;

  constructor(private sessionId: number, private h: QueueHandlers) {
    this.key = `wms_inv_pending_${sessionId}`;
    try {
      const saved = JSON.parse(localStorage.getItem(this.key) || '[]');
      if (Array.isArray(saved) && saved.length) { this.pending = saved; this.schedule(0); }
    } catch { /* unreadable: start clean */ }
    this.report();
  }

  pendingCount(): number { return this.pending.length; }
  isStopped(): boolean { return this.stopped; }

  push(scan: QueuedScan): boolean {
    if (this.stopped) return false;
    this.pending.push(scan);
    this.save(); this.report();
    if (this.pending.length >= MAX_BATCH) this.flushNow();
    else if (!this.timer) this.timer = setTimeout(() => this.flushNow(), FLUSH_MS);
    return true;
  }

  flushNow() {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    void this.send();
  }

  /** Flush now and wait until nothing is pending (or the timeout): the number still pending. */
  async drain(timeoutMs: number): Promise<number> {
    this.flushNow();
    const until = Date.now() + timeoutMs;
    let lastKick = Date.now();
    while (this.pending.length && Date.now() < until && !this.stopped) {
      await new Promise(res => setTimeout(res, 100));
      // re-send at most once a second while draining (review L3: a flush every 100 ms hammered an unreachable server)
      if (!this.sending && this.pending.length && Date.now() - lastKick >= 1000) { lastKick = Date.now(); this.flushNow(); }
    }
    return this.pending.length;
  }

  destroy() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (!this.pending.length) { try { localStorage.removeItem(this.key); } catch { /* fine */ } }
  }

  private save() {
    try {
      if (this.pending.length) localStorage.setItem(this.key, JSON.stringify(this.pending.slice(-2000)));
      else localStorage.removeItem(this.key);
    } catch { /* storage blocked: the in-memory copy still retries */ }
  }
  private report() { this.h.onPending(this.pending.length, this.fails >= 2); }
  private schedule(step?: number) {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (!this.pending.length || this.stopped) { this.retryStep = 0; return; }
    const ms = step !== undefined ? step : RETRY_MS[Math.min(this.retryStep, RETRY_MS.length - 1)];
    this.retryStep += 1;
    this.retryTimer = setTimeout(() => { this.retryTimer = null; void this.send(); }, ms);
  }

  private async send() {
    if (this.sending || this.stopped || !this.pending.length) return;
    this.sending = true;
    try {
      while (this.pending.length && !this.stopped) {
        const batch = this.pending.slice(0, MAX_BATCH);
        let r: Response;
        const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const killer = ctl ? setTimeout(() => ctl.abort(), 15000) : null;     // a slow server never freezes the queue (review M5)
        try {
          r = await authFetch(`${INV}/scan-batch`, { method: 'POST', body: JSON.stringify({ session_id: this.sessionId, scans: batch }), signal: ctl?.signal });
        } catch {
          if (killer) clearTimeout(killer);
          this.fails += 1; this.report(); this.schedule(); return;           // network / timeout: keep, retry
        }
        if (killer) clearTimeout(killer);
        if (this.stopped) return;                                              // destroyed meanwhile: the answer belongs to a screen that is gone
        if (r.ok) {
          const d = await r.json().catch(() => ({}));
          this.settle(batch); this.fails = 0; this.retryStep = 0; this.report();
          try { this.h.onResults(d.results || [], d.lines || [], batch); } catch { /* the screen's problem, not the queue's */ }
          continue;
        }
        if (r.status === 410 || r.status === 409) {
          const d = await r.json().catch(() => ({}));
          const det = d.detail && typeof d.detail === 'object' ? d.detail : {};
          this.stopped = true;
          const unsent = this.pending.slice();
          this.pending = []; this.save(); this.report();
          this.h.onSessionGone(det.reason || (r.status === 409 ? 'conflict' : 'saved'), unsent);
          return;
        }
        if (r.status === 401) {
          this.fails += 2; this.report(); this.h.onError('signed out — log in again; the scans are kept and sent after', []); this.schedule(); return;
        }
        if (r.status >= 400 && r.status < 500 && ![408, 429].includes(r.status)) {
          const d = await r.json().catch(() => ({}));
          this.settle(batch); this.report();                                   // the server refused for good: drop, say so
          this.h.onError(typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`, batch);
          continue;
        }
        this.fails += 1; this.report(); this.schedule(); return;              // 5xx / 401 / 429: keep, retry
      }
    } finally { this.sending = false; }
  }
  private settle(batch: QueuedScan[]) {
    const done = new Set(batch.map(s => s.cid));
    this.pending = this.pending.filter(s => !done.has(s.cid));
    this.save();
  }
}
